// @LTG-ID: data/journals/selling-a-restaurant
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 34 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Industry-Specific (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "selling-a-restaurant",
    FRONTMATTER: {
        number: "71",
        section: "Industry-Specific",
        sectionHref: "/journals#industry-specific",
        sectionAnchor: "industry-specific",
        title: "Selling a Restaurant: The Honest Guide for Independent and Franchise Owners",
        deck: "Restaurants sell on provable earnings, transferable leases, and operations that run without the owner.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-06-24",
        publishedHuman: "June 24, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 454,
        timeRequired: "PT3M",
        keywords: "selling a restaurant, restaurant valuation, how to sell a restaurant, selling a franchise restaurant",
        articleSection: "Industry-Specific",
        articleBodyPreview: "Restaurants sell on provable earnings, transferable leases, and operations that run without the owner. The classic deal killers are unreported cash sales, leases that cannot be assigned on workable terms, and liquor licenses or franchise approvals that stall closings. Preparation means documented numbers and early work on every transfer requirement. Restaurants change hands constantly, yet restaurant sellers are disappointed more often than almost any other owner group. The reasons are specific,",
        complianceNote: "This article is educational and does not constitute legal or financial advice. License and franchise transfer rules vary by jurisdiction and brand.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "how-long-does-it-take-to-sell-a-business",
        "clean-financial-records-business-value",
        "sell-business-confidentially"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Selling a Restaurant: Independent & Franchise Owners · LTG",
        canonical: "https://letstalkgrowth.com/journals/selling-a-restaurant",
        ogImage: "/journals/og/selling-a-restaurant.jpg",
    },
    BODY_MARKDOWN: `Restaurants sell on provable earnings, transferable leases, and operations that run without the owner. The classic deal killers are unreported cash sales, leases that cannot be assigned on workable terms, and liquor licenses or franchise approvals that stall closings. Preparation means documented numbers and early work on every transfer requirement.

Restaurants change hands constantly, yet restaurant sellers are disappointed more often than almost any other owner group. The reasons are specific, predictable, and mostly preventable.

## The provability problem

The restaurant industry's oldest habit, undocumented cash, is its most expensive at sale time. This series has said it generally; here it lands hardest: earnings that never reached the books cannot be sold, because no buyer, and certainly no lender, pays for numbers on faith. Modern point-of-sale systems have improved provability across the industry, and owners two or more years from selling should treat complete reporting and a clean POS record as their single highest-return preparation. The trailing years a buyer will examine are being written now.

## The lease is half the deal

A restaurant's location is often most of its franchise with customers, and the seller usually does not own it. Buyers and their lenders examine remaining lease term, renewal options, rent relative to sales, and, decisively, assignability. Landlord consent is a genuine closing gate: some landlords cooperate, some demand concessions, some see a transaction as their chance to reset rent. Opening the landlord conversation early, and knowing your lease's assignment clause cold before going to market, prevents the late-stage ambush that kills restaurant deals more often than price does.

## The permission stack

Restaurants close through a stack of third-party permissions, each with its own clock: liquor license transfer or reissuance, governed by state and local rules that range from routine to genuinely slow and quota-limited; health permits and inspections; and, for franchise locations, the franchisor's approval of the buyer, training requirements, and transfer fees under the franchise agreement's terms. None of these is exotic, but every one of them has a lead time, and the seller who maps the full stack at the start sets a realistic closing timeline instead of discovering it.

## What earns the stronger price

Beyond provable numbers and a solid lease: management depth, a kitchen and floor that run without the owner present, because buyers know the difference between buying a restaurant and buying a job with a fryer; sales trends that do not depend on the owner's personal following; and equipment in documented working order. Concept portability matters too: the more the operation runs on systems, recipes, and trained staff rather than the owner's daily presence, the wider the buyer pool, and this Journal's owner-dependence article applies to no industry more directly.`,
    FAQ: [
        { question: "Why do so many restaurant sales fall through?", answer: "The recurring causes are unprovable earnings, lease assignment problems, and stalled license or franchise transfers. All three are addressable with preparation: complete reporting, early landlord engagement, and mapping every required permission before going to market." },
        { question: "Can I sell a restaurant with unreported cash sales?", answer: "You can only sell what you can prove. Unreported revenue cannot support price or financing. Owners planning a sale should run fully documented operations for at least two years beforehand so the trailing record reflects the business's real performance." },
        { question: "How does selling a franchise restaurant differ?", answer: "The franchisor becomes a party in practice: buyer approval, training, transfer fees, and current agreement terms all apply, per your franchise agreement. Review the transfer provisions with your attorney at the start, not after finding a buyer." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Exit Planning",
            title: "How Long Does It Take to Sell a Business?",
            meta: "3 min · Brief 43",
            to: "/journals/how-long-does-it-take-to-sell-a-business",
        },
        {
            tag: "Value Drivers",
            title: "Why Clean Financial Records Are Worth Real Money When You Sell",
            meta: "3 min · Brief 51",
            to: "/journals/clean-financial-records-business-value",
        },
        {
            tag: "Deal Process",
            title: "How to Sell Your Business Confidentially (Without Employees or Competitors Finding Out)",
            meta: "3 min · Brief 66",
            to: "/journals/sell-business-confidentially",
        },
    ],
};

export default BRIEF;
